import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronRight, CheckCircle, CreditCard, Clock } from 'lucide-react';

/**
 * TodoGroup — one card per tile. Switches on `kind`:
 *   count — big number
 *   date  — due-date pill with days-until
 *   list  — reserved for v2 (itemised todos)
 *
 * Icon chip matches the employee list on /employees (rounded square with
 * `var(--rgba-primary-1)` background + `var(--bs-primary)` foreground) so
 * the ToDo feels native to the rest of the app.
 */
const DOMAIN_ICON = {
  expenses:      CreditCard,
  time_reports:  Clock,
  tax_reporting: Calendar,
};

// Severity drives only the number / date text color and the action label
// color — no left ribbon, no card tinting.
const SEVERITY_ACCENT = {
  info:   '#1e40af',
  warn:   '#b45309',
  urgent: '#991b1b',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
  } catch { return iso; }
};

const TodoGroup = ({ group }) => {
  const accent = SEVERITY_ACCENT[group.severity] || SEVERITY_ACCENT.info;
  const Icon = DOMAIN_ICON[group.domain] || AlertTriangle;

  return (
    <Link
      to={group.actionHref}
      className="todo-group"
      style={{ textDecoration: 'none' }}
    >
      <div
        className="d-flex align-items-center gap-3"
        style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '14px 16px',
          transition: 'all 0.15s ease',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: 40, height: 40, borderRadius: 8,
            backgroundColor: 'var(--rgba-primary-1)',
            color: 'var(--bs-primary)',
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
            {group.title}
          </div>
          {group.kind === 'count' && (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: accent, marginRight: 6 }}>
                {group.count}
              </span>
              {group.count === 1 ? 'uppgift' : 'uppgifter'}
            </div>
          )}
          {group.kind === 'date' && (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: accent }}>
                {fmtDate(group.dueDate)}
              </span>
              {typeof group.daysUntilDue === 'number' && (
                <span style={{ marginLeft: 8 }}>
                  {group.daysUntilDue === 0 ? 'i dag'
                   : group.daysUntilDue === 1 ? 'i morgon'
                   : group.daysUntilDue > 0 ? `om ${group.daysUntilDue} dagar`
                   : `${Math.abs(group.daysUntilDue)} dagar försenad`}
                </span>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 13, color: 'var(--bs-primary)', fontWeight: 500,
          }}
        >
          {group.actionLabel || 'Öppna'}
          <ChevronRight size={16} />
        </div>
      </div>
    </Link>
  );
};

export default TodoGroup;
